// Copyright 2026 AllSpice. All rights reserved. Not Gitea.
// SPDX-License-Identifier: UNLICENSED

import {createApp, type Component} from 'vue';
import {displayError} from './common.ts';
import {queryElems} from '../utils/dom.ts';
import {decodeComponentTable} from '../utils/drcyComponentTable.ts';

// DRCY's review summary carries its component table in a
// `drcy-component-table` code block, which this swaps for the interactive
// table. A block that cannot be read stays as it is under an error message.
export async function initMarkupCodeDrcyComponentTable(elMarkup: HTMLElement): Promise<void> {
  queryElems(elMarkup, 'code.language-drcy-component-table', async (el) => {
    const pre = el.closest('pre');
    if (!pre || pre.hasAttribute('data-render-done')) return;
    // Marked before the component loads, so a second scan of the same markup
    // cannot mount the table twice.
    pre.setAttribute('data-render-done', 'true');

    // Only a comment DRCY posted gets the table. The server already renders
    // anyone else's block as plain code; this is the second lock, and a block
    // it declines stays readable rather than behind the loading state.
    if (!pre.closest('.comment')?.hasAttribute('data-drcy-comment')) {
      pre.classList.remove('is-loading');
      return;
    }

    let table;
    try {
      table = await decodeComponentTable(el.textContent ?? '');
    } catch (err) {
      displayError(pre, err);
      return;
    }
    let DrcyComponentTable: Component;
    try {
      ({default: DrcyComponentTable} = await import('../components/DrcyComponentTable.vue'));
    } catch (err) {
      // A chunk gone stale after a deploy: the reader gets a message and the
      // source, not a spinner that never ends.
      console.error(err);
      displayError(pre, new Error('The DRCY component table could not be loaded. Reload the page to try again.'));
      return;
    }
    const target = document.createElement('div');
    (pre.closest('.code-block-container') ?? pre).replaceWith(target);
    createApp(DrcyComponentTable, {table}).mount(target);
  });
}
